import type {Editor} from 'grapesjs';
import {isMouseWheelEvent} from '@Utils/dom.util';
import {getDistanceBetweenPoints} from '@Utils/math.util';
import {fixLoadingOverlayForEmailSize} from '@EmailMakerReact/lib/email-maker-overlay-library';
import {clampZoom, getCurrentZoom, setZoomDuringGesture} from '@EmailMakerReact/lib/email-maker-zoom-library';

/**
 * Pinch-to-zoom for the email maker canvas: trackpad pinch / ctrl+scroll-wheel on desktop and
 * two-finger touch pinch on mobile. One-finger touches are never intercepted, so native canvas
 * scrolling, tap-to-select and double-tap-to-edit keep working.
 */

const TRACKPAD_PINCH_SENSITIVITY = 150;
const MOUSE_WHEEL_SENSITIVITY_MULTIPLIER = 1.5;
const PINCH_START_DISTANCE_RATIO_THRESHOLD = 0.02;
const ZOOM_GESTURE_SETTLE_DELAY_MS = 150;
const PINCH_TOUCH_COUNT = 2;

interface PinchGestureState {
  startDistance: number;
  startZoom: number;
  hasStarted: boolean;
}

/** Wires wheel + touch pinch zooming on the canvas document and canvas area; returns a detach function. */
export function attachZoomGestures(editor: Editor, canvasArea: HTMLElement): () => void {
  const canvasDocument = editor.Canvas.getDocument();
  if (!canvasDocument) {
    return (): void => {
      // No-op: nothing was attached because the canvas document did not exist.
    };
  }

  let pinchState: PinchGestureState | null = null;
  let gestureSettleTimeoutId: number | null = null;

  const cancelPendingGestureSettle = (): void => {
    if (gestureSettleTimeoutId !== null) {
      window.clearTimeout(gestureSettleTimeoutId);
      gestureSettleTimeoutId = null;
    }
  };

  const setBodyTransition = (transitionValue: string): void => {
    cancelPendingGestureSettle();
    const editorBody = editor.Canvas.getBody();
    if (editorBody) {
      editorBody.style.transition = transitionValue;
    }
  };

  const suspendZoomTransition = (): void => {
    setBodyTransition('none');
  };

  const restoreZoomTransition = (): void => {
    setBodyTransition('');
  };

  const settleZoomGesture = (): void => {
    restoreZoomTransition();
    fixLoadingOverlayForEmailSize(editor);
  };

  const scheduleZoomGestureSettle = (): void => {
    cancelPendingGestureSettle();
    gestureSettleTimeoutId = window.setTimeout(settleZoomGesture, ZOOM_GESTURE_SETTLE_DELAY_MS);
  };

  const applyAnchoredZoom = (newZoom: number, canvasAnchorY: number): void => {
    const currentZoom = getCurrentZoom(editor);
    const editorBody = editor.Canvas.getBody();
    if (newZoom === currentZoom || !editorBody) {
      return;
    }
    suspendZoomTransition();
    // scrollTop is in unscaled layout px while the anchor is a visual offset, hence the /zoom conversions.
    const anchoredScrollTop = editorBody.scrollTop + canvasAnchorY / currentZoom - canvasAnchorY / newZoom;
    setZoomDuringGesture(editor, newZoom);
    editorBody.scrollTop = anchoredScrollTop;
  };

  const zoomOnWheel = (e: WheelEvent, canvasAnchorY: number): void => {
    e.preventDefault();
    const currentZoom = getCurrentZoom(editor);
    const sensitivity = isMouseWheelEvent(e) ? TRACKPAD_PINCH_SENSITIVITY * MOUSE_WHEEL_SENSITIVITY_MULTIPLIER : TRACKPAD_PINCH_SENSITIVITY;
    const zoomFactor = 1 + Math.abs(e.deltaY) / sensitivity;
    const newZoom = clampZoom(e.deltaY > 0 ? currentZoom / zoomFactor : currentZoom * zoomFactor);
    applyAnchoredZoom(newZoom, canvasAnchorY);
    scheduleZoomGestureSettle();
  };

  const onCanvasDocumentWheel = (e: WheelEvent): void => {
    if (!isPinchZoomWheelEvent(e)) {
      return;
    }
    zoomOnWheel(e, e.clientY);
  };

  const onCanvasAreaWheel = (e: WheelEvent): void => {
    const frameElement = editor.Canvas.getFrameEl();
    if (!isPinchZoomWheelEvent(e) || !frameElement) {
      return;
    }
    const frameRect = frameElement.getBoundingClientRect();
    const canvasAnchorY = Math.min(Math.max(e.clientY - frameRect.top, 0), frameRect.height);
    zoomOnWheel(e, canvasAnchorY);
  };

  const abandonPinchGesture = (): void => {
    if (pinchState?.hasStarted) {
      settleZoomGesture();
    }
    pinchState = null;
  };

  const onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== PINCH_TOUCH_COUNT || isBrowserPagePinchZoomed()) {
      abandonPinchGesture();
      return;
    }
    pinchState = {
      startDistance: getTouchDistance(e.touches),
      startZoom: getCurrentZoom(editor),
      hasStarted: false,
    };
  };

  const onTouchMove = (e: TouchEvent): void => {
    if (!pinchState || e.touches.length !== PINCH_TOUCH_COUNT) {
      return;
    }
    const distanceRatio = getTouchDistance(e.touches) / pinchState.startDistance;
    if (!pinchState.hasStarted && Math.abs(distanceRatio - 1) < PINCH_START_DISTANCE_RATIO_THRESHOLD) {
      return;
    }
    pinchState.hasStarted = true;
    e.preventDefault();
    applyAnchoredZoom(clampZoom(pinchState.startZoom * distanceRatio), getTouchMidpointY(e.touches));
  };

  const onTouchEnd = (e: TouchEvent): void => {
    if (e.touches.length >= PINCH_TOUCH_COUNT) {
      return;
    }
    abandonPinchGesture();
  };

  canvasDocument.addEventListener('wheel', onCanvasDocumentWheel, {passive: false});
  canvasDocument.addEventListener('touchstart', onTouchStart, {passive: true});
  canvasDocument.addEventListener('touchmove', onTouchMove, {passive: false});
  canvasDocument.addEventListener('touchend', onTouchEnd, {passive: true});
  canvasDocument.addEventListener('touchcancel', onTouchEnd, {passive: true});
  canvasArea.addEventListener('wheel', onCanvasAreaWheel, {passive: false});

  return (): void => {
    canvasDocument.removeEventListener('wheel', onCanvasDocumentWheel);
    canvasDocument.removeEventListener('touchstart', onTouchStart);
    canvasDocument.removeEventListener('touchmove', onTouchMove);
    canvasDocument.removeEventListener('touchend', onTouchEnd);
    canvasDocument.removeEventListener('touchcancel', onTouchEnd);
    canvasArea.removeEventListener('wheel', onCanvasAreaWheel);
    restoreZoomTransition();
  };
}

/** Trackpad pinches arrive as ctrl+wheel; skipped while the browser page itself is pinch-zoomed. */
function isPinchZoomWheelEvent(e: WheelEvent): boolean {
  return e.ctrlKey && !isBrowserPagePinchZoomed();
}

function isBrowserPagePinchZoomed(): boolean {
  return (window.visualViewport?.scale ?? 1) !== 1;
}

function getTouchDistance(touches: TouchList): number {
  return getDistanceBetweenPoints({x: touches[0].clientX, y: touches[0].clientY}, {x: touches[1].clientX, y: touches[1].clientY});
}

function getTouchMidpointY(touches: TouchList): number {
  return (touches[0].clientY + touches[1].clientY) / 2;
}
